import styled from '@emotion/styled'

import { Theme } from 'component-library/__styling/themes'
import { colors, zIndex } from 'component-library/__styling/settings'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'

import { VideoMetadata } from 'component-library/content/Video/components/common/UnstyledVideoMetadata'
import { themedValue } from 'component-library/__styling/themed-value'
import { hexToRGBA } from 'component-library/__styling/style-mixins'
import { playButtonClass } from 'component-library/buttons/PlayButton/PlayButton.styled'

export interface StyledTitleOverlayProps {
    compactMode: boolean
}

const themedColor = (theme: Theme) =>
    themedValue(theme, {
        thewest: colors.black,
        perthnow: colors.white,
        sevennews: colors.white,
    })

const primaryColor = (theme: Theme) =>
    themedValue(theme, {
        thewest: section => section.primaryColor,
        perthnow: colors.perthnow.pinkThulite,
        sevennews: resolvedSection => resolvedSection.video.primaryColor,
    })

export const StyledTitleOverlay = styled('div')<StyledTitleOverlayProps>(
    {
        display: 'flex',
        alignItems: 'center',
        zIndex: zIndex.common.videoTitleOverlay,
        right: 0,
        bottom: 0,
        left: 0,
        position: 'absolute',
        paddingLeft: calcRem(75),
        paddingRight: calcRem(15),
        height: calcRem(80),

        [breakpoint('sm')]: {
            paddingLeft: calcRem(120),
        },
    },
    ({ theme }) => ({
        [breakpoint('sm')]: {
            height: themedValue(theme, {
                sevennews: calcRem(120),
                fallback: calcRem(100),
            }),
            paddingLeft: themedValue(theme, {
                sevennews: calcRem(110),
                fallback: calcRem(120),
            }),
        },
        fontFamily: themedValue(theme, {
            sevennews: theme.fonts.sansSerifCond,
            fallback: theme.fonts.sansSerif,
        }),
        background: themedValue(theme, {
            thewest: `hsla(0,  0%, 100%, .9)`,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: hexToRGBA(colors.sevennews.charade, 0.9),
        }),
        color: themedColor(theme),

        [`& .${playButtonClass}`]: {
            bottom: themedValue(theme, {
                sevennews: calcRem(18),
                fallback: undefined,
            }),
            [breakpoint('sm')]: {
                bottom: themedValue(theme, {
                    sevennews: calcRem(30),
                    fallback: undefined,
                }),
            },
        },
    }),
    ({ compactMode, theme }) =>
        compactMode
            ? {
                  paddingLeft: themedValue(theme, {
                      sevennews: calcRem(100),
                      fallback: calcRem(112),
                  }),
              }
            : {}
)

export const StyledInset = styled('div')(
    {
        lineHeight: 1,
        width: '100%',
        maxWidth: '100%',

        [breakpoint('sm')]: {
            transform: 'none',
            paddingTop: 0,
            paddingBottom: 0,
        },
    },
    ({ theme }) => ({
        // align to the play button (it's misaligned)
        // TODO: fix misaligned play button
        transform: themedValue(theme, {
            sevennews: undefined,
            fallback: `translateY(${calcRem(3)})`,
        }),
        padding: themedValue(theme, {
            sevennews: `${calcRem(4)} 0`,
            fallback: `${calcRem(4)} 0 ${calcRem(4)} ${calcRem(10)}`,
        }),
        borderLeft: themedValue(theme, {
            sevennews: undefined,
            fallback: `${calcRem(4)} solid ${primaryColor(theme)}`,
        }),
    })
)

export const StyledTitles = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
})

export interface StyledHeadingProps {
    autoPlayIsEnabled: boolean
}

export const StyledHeading = styled('h3')<StyledHeadingProps>(
    ({ theme }) => ({
        fontSize: themedValue(theme, {
            sevennews: calcRem(14),
            fallback: calcRem(18),
        }),
        overflow: 'hidden',
        margin: themedValue(theme, {
            sevennews: calcRem(4, 0, 0),
            fallback: 0,
        }),
        padding: 0,
        flex: '1 1 70%',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.2, // need to set this because of the overflow hidden
        verticalAlign: 'middle',

        [breakpoint('md')]: {
            fontSize: themedValue(theme, {
                sevennews: calcRem(17),
                fallback: calcRem(22),
            }),
        },
    }),

    (props: StyledHeadingProps) => {
        return {
            ...conditionalStyles(props.autoPlayIsEnabled, {
                // if autoplay is enabled, we don't show the titles on countdown on mobile
                display: 'none',
                [breakpoint('sm')]: {
                    display: 'block',
                },
            }),
        }
    }
)

export const StyledVideoMetadata = styled(VideoMetadata)(({ theme }) => ({
    display: 'none',
    margin: themedValue(theme, {
        sevennews: 0,
        fallback: `0 0 0 ${calcRem(15)}`,
    }),
    flexBasis: themedValue(theme, {
        sevennews: '100%',
        fallback: undefined,
    }),
    padding: 0,
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',

    [breakpoint('sm')]: {
        display: 'block',
        fontSize: calcRem(14),
    },

    [breakpoint('md')]: {
        fontSize: calcRem(16),
    },

    ...conditionalStyles(theme.kind === 'sevennews', {
        time: {
            fontWeight: 600,
        },
    }),
}))

export const StyledAutoplayWrap = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    [breakpoint('sm')]: {
        marginTop: themedValue(theme, {
            sevennews: calcRem(4),
            fallback: calcRem(6),
        }),
    },
    fontFamily: themedValue(theme, {
        sevennews: theme.fonts.sansSerif,
        fallback: undefined,
    }),
}))

export const StyledCountdown = styled('p')(({ theme }) => ({
    fontSize: themedValue(theme, {
        sevennews: calcRem(14),
        fallback: calcRem(16),
    }),
    margin: themedValue(theme, {
        sevennews: `0 ${calcRem(8)} 0 0`,
        fallback: `0 ${calcRem(15)} 0 0`,
    }),
    lineHeight: 1,
}))

export const StyledCountdownTime = styled('span')({
    fontWeight: 700,
})

export const StyledCancel = styled('button')(({ theme }) => ({
    margin: 0,
    padding: 0,
    lineHeight: 1,
    cursor: 'pointer',

    // on the old video player these came from brightcove
    background: 'none',
    border: 'none',
    color: themedColor(theme),
    fontSize: themedValue(theme, {
        sevennews: calcRem(13),
        fallback: calcRem(16),
    }),
    fontWeight: themedValue(theme, {
        sevennews: 400,
        fallback: 700,
    }),
    textTransform: themedValue(theme, {
        sevennews: undefined,
        fallback: 'uppercase' as 'uppercase',
    }),
    textDecoration: themedValue(theme, {
        sevennews: 'underline',
        fallback: undefined,
    }),
    '&::before': {
        display: themedValue(theme, {
            sevennews: 'none',
            fallback: 'inline-block',
        }),
        marginRight: calcRem(5),
        width: calcRem(16),
        height: calcRem(16),
        content: `''`,
        verticalAlign: 'top',
        background: themedValue(theme, {
            thewest: colors.black,
            perthnow: colors.white,
            sevennews: colors.sevennews.charade,
        }),
    },
    '&:hover': {
        color: primaryColor(theme),
    },

    [breakpoint('sm')]: {
        display: 'inline-block',
    },
}))
